import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Check, ArrowRight, CheckCircle2 } from 'lucide-react';
interface WaitlistModalProps {
  isOpen: boolean;
  onClose: () => void;
  email: string;
}
const features = ['Automated Resume Screening', 'Psychometric Driver Evaluation', 'AI Phone Screening', 'Safe Driver Profile Identification', 'Automated Interview Scheduling', 'Dashboard & Analytics', 'Automated Candidate Communication', '24/7 Hiring Pipeline'];
const employeeCounts = ['1–10', '11–50', '51–200', '201–500', '500+'];
const hireCounts = ['1–5', '6–15', '16–50', '51–100', '100+'];
export function WaitlistModal({
  isOpen,
  onClose,
  email
}: WaitlistModalProps) {
  const [step, setStep] = useState(1);
  const [selectedFeatures, setSelectedFeatures] = useState<string[]>([]);
  const [additionalFeedback, setAdditionalFeedback] = useState('');
  const [employeeCount, setEmployeeCount] = useState('');
  const [hireCount, setHireCount] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  if (!isOpen) return null;
  const toggleFeature = (feature: string) => {
    setSelectedFeatures(prev => prev.includes(feature) ? prev.filter(f => f !== feature) : [...prev, feature]);
  };
  const handleNext = () => {
    setStep(2);
  };
  const handleSubmit = async () => {
    setIsSubmitting(true);
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 1000));
    setIsSubmitting(false);
    setStep(3);
  };
  const handleClose = () => {
    setStep(1);
    setSelectedFeatures([]);
    setAdditionalFeedback('');
    setEmployeeCount('');
    setHireCount('');
    onClose();
  };
  return <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm" data-id="element-154">
      <motion.div initial={{
      opacity: 0,
      scale: 0.95,
      y: 20
    }} animate={{
      opacity: 1,
      scale: 1,
      y: 0
    }} exit={{
      opacity: 0,
      scale: 0.95,
      y: 20
    }} className="relative w-full max-w-2xl bg-white rounded-2xl shadow-2xl overflow-hidden flex flex-col max-h-[90vh]" data-id="element-155">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100" data-id="element-156">
          <div className="flex gap-2" data-id="element-157">
            {[1, 2, 3].map(i => <div key={i} className={`h-1.5 w-8 rounded-full transition-colors ${i <= step ? 'bg-blue-500' : 'bg-slate-100'}`} data-id="element-158" />)}
          </div>
          <button onClick={handleClose} className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-50 rounded-full transition-colors" data-id="element-159">
            <X className="w-5 h-5" data-id="element-160" />
          </button>
        </div>

        {/* Content Area - Scrollable */}
        <div className="p-6 sm:p-8 overflow-y-auto" data-id="element-161">
          <AnimatePresence mode="wait" data-id="element-162">
            {step === 1 && <motion.div key="step1" initial={{
            opacity: 0,
            x: 20
          }} animate={{
            opacity: 1,
            x: 0
          }} exit={{
            opacity: 0,
            x: -20
          }} className="space-y-8" data-id="element-163">
                <div data-id="element-164">
                  <h2 className="text-2xl font-display font-bold text-slate-900 mb-2" data-id="element-165">
                    Thanks for signing up! 🎉
                  </h2>
                  <p className="text-slate-600" data-id="element-166">
                    Help us build the right product for you. Which features
                    matter most?
                  </p>
                </div>

                <div className="grid sm:grid-cols-2 gap-3" data-id="element-167">
                  {features.map(feature => {
                const isSelected = selectedFeatures.includes(feature);
                return <button key={feature} onClick={() => toggleFeature(feature)} className={`flex items-start gap-3 p-3 rounded-xl border text-left transition-all ${isSelected ? 'border-blue-500 bg-blue-50 text-blue-900 shadow-sm' : 'border-slate-200 hover:border-slate-300 hover:bg-slate-50 text-slate-700'}`} data-id="element-168">
                        <div className={`mt-0.5 flex-shrink-0 w-5 h-5 rounded border flex items-center justify-center transition-colors ${isSelected ? 'bg-blue-500 border-blue-500 text-white' : 'border-slate-300 bg-white'}`} data-id="element-169">
                          {isSelected && <Check className="w-3.5 h-3.5" data-id="element-170" />}
                        </div>
                        <span className="text-sm font-medium leading-tight" data-id="element-171">
                          {feature}
                        </span>
                      </button>;
              })}
                </div>

                <div data-id="element-172">
                  <label className="block text-sm font-medium text-slate-900 mb-2" data-id="element-173">
                    Anything else you want from a hiring solution?
                  </label>
                  <textarea value={additionalFeedback} onChange={e => setAdditionalFeedback(e.target.value)} placeholder="Tell us what matters most to you..." className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-slate-900 placeholder:text-slate-400 resize-none h-24" data-id="element-174" />
                </div>

                <div className="flex justify-end pt-4 border-t border-slate-100" data-id="element-175">
                  <button onClick={handleNext} className="inline-flex items-center justify-center px-6 py-3 font-semibold rounded-lg bg-blue-500 text-white hover:bg-blue-400 transition-colors" data-id="element-176">
                    Continue
                    <ArrowRight className="w-4 h-4 ml-2" data-id="element-177" />
                  </button>
                </div>
              </motion.div>}

            {step === 2 && <motion.div key="step2" initial={{
            opacity: 0,
            x: 20
          }} animate={{
            opacity: 1,
            x: 0
          }} exit={{
            opacity: 0,
            x: -20
          }} className="space-y-8" data-id="element-178">
                <div data-id="element-179">
                  <h2 className="text-2xl font-display font-bold text-slate-900 mb-2" data-id="element-180">
                    Almost done — tell us about your company
                  </h2>
                  <p className="text-slate-600" data-id="element-181">
                    This helps us prioritize onboarding for fleets of different
                    sizes.
                  </p>
                </div>

                <div className="space-y-6" data-id="element-182">
                  <div data-id="element-183">
                    <label className="block text-sm font-medium text-slate-900 mb-3" data-id="element-184">
                      How many employees does your company have?
                    </label>
                    <div className="flex flex-wrap gap-3" data-id="element-185">
                      {employeeCounts.map(count => <button key={count} onClick={() => setEmployeeCount(count)} className={`px-4 py-2 rounded-full text-sm font-medium border transition-all ${employeeCount === count ? 'border-blue-500 bg-blue-50 text-blue-700' : 'border-slate-200 text-slate-600 hover:border-slate-300 hover:bg-slate-50'}`} data-id="element-186">
                          {count}
                        </button>)}
                    </div>
                  </div>

                  <div data-id="element-187">
                    <label className="block text-sm font-medium text-slate-900 mb-3" data-id="element-188">
                      How many hires do you make per month?
                    </label>
                    <div className="flex flex-wrap gap-3" data-id="element-189">
                      {hireCounts.map(count => <button key={count} onClick={() => setHireCount(count)} className={`px-4 py-2 rounded-full text-sm font-medium border transition-all ${hireCount === count ? 'border-blue-500 bg-blue-50 text-blue-700' : 'border-slate-200 text-slate-600 hover:border-slate-300 hover:bg-slate-50'}`} data-id="element-190">
                          {count}
                        </button>)}
                    </div>
                  </div>
                </div>

                <div className="flex justify-between pt-4 border-t border-slate-100" data-id="element-191">
                  <button onClick={() => setStep(1)} className="px-6 py-3 font-medium text-slate-600 hover:text-slate-900 transition-colors" data-id="element-192">
                    Back
                  </button>
                  <button onClick={handleSubmit} disabled={!employeeCount || !hireCount || isSubmitting} className="inline-flex items-center justify-center px-6 py-3 font-semibold rounded-lg bg-blue-500 text-white hover:bg-blue-400 transition-colors disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-193">
                    {isSubmitting ? 'Submitting...' : 'Submit'}
                  </button>
                </div>
              </motion.div>}

            {step === 3 && <motion.div key="step3" initial={{
            opacity: 0,
            scale: 0.95
          }} animate={{
            opacity: 1,
            scale: 1
          }} className="py-12 text-center space-y-6" data-id="element-194">
                <motion.div initial={{
              scale: 0
            }} animate={{
              scale: 1
            }} transition={{
              type: 'spring',
              bounce: 0.5,
              delay: 0.2
            }} className="w-20 h-20 mx-auto bg-green-100 rounded-full flex items-center justify-center" data-id="element-195">
                  <CheckCircle2 className="w-10 h-10 text-green-600" data-id="element-196" />
                </motion.div>

                <div data-id="element-197">
                  <h2 className="text-3xl font-display font-bold text-slate-900 mb-2" data-id="element-198">
                    You're on the list!
                  </h2>
                  <p className="text-slate-600 max-w-sm mx-auto" data-id="element-199">
                    We've saved your preferences. We'll be in touch soon with
                    your early access invitation to <strong data-id="element-200">{email}</strong>.
                  </p>
                </div>

                <button onClick={handleClose} className="inline-flex items-center justify-center px-8 py-3 font-semibold rounded-lg bg-slate-100 text-slate-900 hover:bg-slate-200 transition-colors" data-id="element-201">
                  Close
                </button>
              </motion.div>}
          </AnimatePresence>
        </div>
      </motion.div>
    </div>;
}